import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const KEY = "mm-voice-replies";
const FADE_MS = 250;

// Gentle volume ramp so replies don't start or stop abruptly (phones that lock volume just skip it)
export const fadeAudio = (a, to, ms = FADE_MS) => new Promise((done) => {
  const from = a.volume;
  const t0 = performance.now();
  const step = (t) => {
    const k = Math.min(1, (t - t0) / ms);
    a.volume = Math.max(0, Math.min(1, from + (to - from) * k));
    if (k < 1) requestAnimationFrame(step); else done();
  };
  requestAnimationFrame(step);
});

// Mia's spoken replies: on/off (remembered on this device), play, replay (cached) and stop
export const useMiaVoice = () => {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY) !== "off");
  const [playing, setPlaying] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const audio = useRef(null);
  const cache = useRef(new Map());

  const stop = useCallback(() => {
    const a = audio.current;
    audio.current = null;
    setPlaying(null);
    if (a && !a.paused) fadeAudio(a, 0, 180).then(() => a.pause()); else a?.pause();
  }, []);

  const toggle = () => setEnabled((on) => { localStorage.setItem(KEY, on ? "off" : "on"); if (on) stop(); return !on; });

  const play = useCallback(async (key, text) => {
    stop();
    let url = cache.current.get(key);
    if (!url) {
      setLoadingKey(key);
      try {
        const { data } = await api.post("/ai/speak", { text: text.slice(0, 1500) }, { responseType: "blob" });
        url = URL.createObjectURL(data);
        cache.current.set(key, url);
      } catch (e) {
        toast.error(errMsg(e, "Mia's voice isn't available right now. Her reply is shown as text."));
        return;
      } finally { setLoadingKey(null); }
    }
    const a = new Audio(url);
    audio.current = a;
    a.onended = () => { if (audio.current === a) setPlaying(null); };
    setPlaying(key);
    a.volume = 0;
    a.play().then(() => fadeAudio(a, 1)).catch(() => { setPlaying(null); toast.message("Couldn't play audio on this device. Check your sound settings, then tap Play."); });
  }, [stop]);

  useEffect(() => () => { audio.current?.pause(); cache.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  return { enabled, toggle, play, stop, playing, loadingKey };
};
